import React, { useEffect, useState } from 'react';
import { Alert, Container, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { ConversationHandle } from 'components/Conversation';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import * as Types from 'types/types';
import { BasicInfoTab } from './profile/ProfileBasicInfo';
import { ExperienceDialog, QuestionDialog, SkillDialog } from './profile/ProfileDialogs';
import { ProfileEducationTab } from './profile/ProfileEducationTab';
import { ProfileExperienceTab } from './profile/ProfileExperienceTab';
import { ProfileQuestionsTab } from './profile/ProfileQuestionsTab';
import { ProfileSkillsTab } from './profile/ProfileSkillsTab';
import { ProfileTabs, TabPanel } from './profile/ProfileTabs';

function freshSkill(): Partial<Types.Skill> {
  return { name: '', category: '', level: 'beginner', yearsOfExperience: 0 };
}

function freshExperience(): Partial<Types.WorkExperience> {
  return {
    companyName: '',
    position: '',
    startDate: new Date(),
    isCurrent: false,
    description: '',
    skills: [],
    location: { text: '' },
  };
}

const CandidateProfile: React.FC = () => {
  const theme = useTheme();
  const { setSnack } = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, updateUserData, apiClient } = useAuth();
  const candidate = user?.userType === 'candidate' ? (user as Types.Candidate) : null;
  const chatRef = React.useRef<ConversationHandle>(null);

  const [tabValue, setTabValue] = useState('info');
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [formData, setFormData] = useState<Partial<Types.Candidate>>({});
  const [profileImage, setProfileImage] = useState('');
  const [skillDialog, setSkillDialog] = useState(false);
  const [experienceDialog, setExperienceDialog] = useState(false);
  const [newSkill, setNewSkill] = useState<Partial<Types.Skill>>(freshSkill());
  const [newQuestion, setNewQuestion] = useState<Partial<Types.CandidateQuestion>>({
    question: '',
  });
  const [newExperience, setNewExperience] = useState<Partial<Types.WorkExperience>>(
    freshExperience()
  );

  useEffect(() => {
    if (!candidate) {
      return;
    }
    setFormData(candidate);
    setProfileImage(
      candidate.profileImage ? `/api/1.0/candidates/profile/${candidate.username}` : ''
    );
  }, [candidate]);

  const toggleEditMode = (section: string): void => {
    setEditMode(current => ({ ...current, [section]: !current[section] }));
  };

  const handleInputChange = (field: string, value: boolean | string | Types.Location): void => {
    setFormData(current => ({ ...current, [field]: value }));
  };

  const handleSave = async (): Promise<void> => {
    if (!candidate?.id) {
      return;
    }

    setLoading(true);
    try {
      const updatedCandidate = await apiClient.updateCandidate(candidate.id, formData);
      updateUserData(updatedCandidate);
      setSnack('Profile updated successfully!');
    } catch {
      setSnack('Failed to update profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dirty) {
      return;
    }
    handleSave();
    setDirty(false);
  }, [formData, dirty]);

  if (!candidate) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Access denied. This page is only available for candidates.</Alert>
      </Container>
    );
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!e.target.files?.[0]) {
      return;
    }

    try {
      const file = e.target.files[0];
      const success = await apiClient.uploadCandidateProfile(file);
      if (!success) {
        setSnack('Failed to upload profile image. Please try again.', 'error');
        return;
      }

      setProfileImage(URL.createObjectURL(file));
      updateUserData({ ...candidate, profileImage: `profile.${file.name.replace(/^.*\./, '')}` });
    } catch {
      setSnack('Failed to upload profile image. Please try again.', 'error');
    }
  };

  const handleAddSkill = (): void => {
    if (!newSkill.name || !newSkill.category) {
      return;
    }
    setFormData(current => ({
      ...current,
      skills: [...(current.skills || []), newSkill as Types.Skill],
    }));
    setNewSkill(freshSkill());
    setSkillDialog(false);
    setSnack('Skill added successfully!');
    setDirty(true);
  };

  const handleRemoveSkill = (index: number): void => {
    setFormData(current => ({
      ...current,
      skills: (current.skills || []).filter((_, i) => i !== index),
    }));
    setSnack('Skill removed successfully!');
    setDirty(true);
  };

  const handleAddQuestion = (): void => {
    const question = newQuestion.question?.trim();
    if (!question) {
      return;
    }
    setFormData(current => ({
      ...current,
      questions: [...(current.questions || []), { question }],
    }));
    setNewQuestion({ question: '' });
    setEditMode(current => ({ ...current, questions: false }));
    setSnack('Question added successfully!');
    setDirty(true);
  };

  const handleRemoveQuestion = (index: number): void => {
    setFormData(current => ({
      ...current,
      questions: (current.questions || []).filter((_, i) => i !== index),
    }));
    setSnack('Question removed successfully!');
    setDirty(true);
  };

  const handleAddExperience = (): void => {
    if (!newExperience.companyName || !newExperience.position) {
      return;
    }
    setFormData(current => ({
      ...current,
      experience: [...(current.experience || []), newExperience as Types.WorkExperience],
    }));
    setNewExperience(freshExperience());
    setExperienceDialog(false);
    setSnack('Experience added successfully!');
    setDirty(true);
  };

  const handleRemoveExperience = (index: number): void => {
    setFormData(current => ({
      ...current,
      experience: (current.experience || []).filter((_, i) => i !== index),
    }));
    setSnack('Experience removed successfully!');
    setDirty(true);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: { xs: 1, sm: 4 }, mb: { xs: 1, sm: 4 }, px: { xs: 0.5, sm: 3 } }}
    >
      <Paper elevation={3} sx={{ overflow: 'hidden', mx: { xs: 0, sm: 0 } }}>
        <ProfileTabs
          tabValue={tabValue}
          isMobile={isMobile}
          onChange={(_, value): void => setTabValue(value)}
        />
        <TabPanel value="info" active={tabValue}>
          <BasicInfoTab
            candidate={candidate}
            formData={formData}
            editMode={editMode}
            isMobile={isMobile}
            loading={loading}
            profileImage={profileImage}
            primaryColor={theme.palette.primary.main}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            handleImageUpload={handleImageUpload}
            handleCancel={(): void => {
              setFormData(candidate);
              toggleEditMode('basic');
            }}
            handleSave={handleSave}
            toggleEditMode={toggleEditMode}
          />
        </TabPanel>
        <TabPanel value="questions" active={tabValue}>
          <ProfileQuestionsTab
            questions={formData.questions}
            isMobile={isMobile}
            chatRef={chatRef}
            onAddQuestion={(): void => setEditMode(current => ({ ...current, questions: true }))}
            onRemoveQuestion={handleRemoveQuestion}
          />
        </TabPanel>
        <TabPanel value="skills" active={tabValue}>
          <ProfileSkillsTab
            skills={formData.skills}
            isMobile={isMobile}
            onAddSkill={(): void => setSkillDialog(true)}
            onRemoveSkill={handleRemoveSkill}
          />
        </TabPanel>
        <TabPanel value="experience" active={tabValue}>
          <ProfileExperienceTab
            experience={formData.experience}
            isMobile={isMobile}
            onAddExperience={(): void => setExperienceDialog(true)}
            onRemoveExperience={handleRemoveExperience}
          />
        </TabPanel>
        <TabPanel value="education" active={tabValue}>
          <ProfileEducationTab isMobile={isMobile} />
        </TabPanel>
      </Paper>
      <SkillDialog
        open={skillDialog}
        isMobile={isMobile}
        newSkill={newSkill}
        onSkillChange={setNewSkill}
        onClose={(): void => setSkillDialog(false)}
        onAdd={handleAddSkill}
      />
      <QuestionDialog
        open={!!editMode.questions}
        isMobile={isMobile}
        newQuestion={newQuestion}
        onQuestionChange={setNewQuestion}
        onClose={(): void => setEditMode(current => ({ ...current, questions: false }))}
        onAdd={handleAddQuestion}
      />
      <ExperienceDialog
        open={experienceDialog}
        isMobile={isMobile}
        newExperience={newExperience}
        onExperienceChange={setNewExperience}
        onClose={(): void => setExperienceDialog(false)}
        onAdd={handleAddExperience}
      />
    </Container>
  );
};

export { CandidateProfile };
